import { AlertTriangle, Eye, EyeOff } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation } from "react-router-dom";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { zodResolver } from "@/lib/form";
import { MIN_PASSWORD, tokenFromHash } from "@/lib/invitations";
import { landingPath } from "@/lib/nav";
import { Notice, Shell } from "@/routes/invite";
import { formatDateTime } from "@/lib/utils";

/**
 * The page a reset link opens (ADR-057 in `platform`): check the link, choose a password, signed in.
 *
 * The token is the link's fragment, `/reset-password#<token>`, as an invitation's is. A browser
 * never sends a fragment to a server, so it reaches core-api only in the body of the two requests
 * this page makes.
 *
 * Completing remounts everything under `AuthProvider`, this page included, so the page cannot keep
 * a flag saying it has just finished. It needs none: a signed-in reader here is the person whose
 * password was just set, and goes on to their landing page.
 */
export function ResetPasswordPage() {
  const { user, loading } = useAuth();
  const { hash } = useLocation();
  const token = tokenFromHash(hash);
  const [attempt, setAttempt] = React.useState(0);
  const link = useLink(token, attempt);

  if (loading) return null;
  // Set, signed in, remounted. Also the case where somebody opens a link in a browser that is
  // already signed in as the same person, which is harmless.
  if (user) return <Navigate to={landingPath(user.role)} replace />;

  if (!token) {
    return (
      <Shell title="Reset your password">
        <Notice
          title="This link is incomplete"
          body="Open the link from your email again. If it still does not work, ask for a new one."
          action={
            <Button variant="default" asChild>
              <Link to="/forgot-password">Ask for a new link</Link>
            </Button>
          }
        />
      </Shell>
    );
  }

  if (link.state === "loading") {
    return (
      <Shell title="Reset your password">
        <div className="flex justify-center py-8">
          <Spinner className="size-5" />
        </div>
      </Shell>
    );
  }

  if (link.state === "failed") {
    return (
      <Shell title="Reset your password">
        <Notice
          title="Could not check this link"
          body={link.error instanceof Error ? link.error.message : "Check your connection and try again."}
          action={
            <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </Button>
          }
        />
      </Shell>
    );
  }

  if (!link.check.usable) {
    // One message for expired, spent and replaced. The server does not say which, on purpose: a
    // link that is not good must not answer questions about the account it was for.
    return (
      <Shell title="Reset your password">
        <Notice
          title="This link no longer works"
          body="A reset link works once and for an hour, and asking for a new one replaces the last. Ask for another and use the newest email."
          action={
            <Button variant="default" asChild>
              <Link to="/forgot-password">Ask for a new link</Link>
            </Button>
          }
        />
      </Shell>
    );
  }

  return (
    <Shell title="Choose a new password" subtitle={link.check.email ?? undefined}>
      <ResetForm
        token={token}
        email={link.check.email ?? ""}
        expiresAt={link.check.expires_at}
        onGone={() => setAttempt((n) => n + 1)}
      />
    </Shell>
  );
}

interface LinkCheck {
  usable: boolean;
  email: string | null;
  expires_at: string | null;
}

type Checked =
  | { state: "loading" }
  | { state: "ready"; check: LinkCheck }
  | { state: "failed"; error: unknown };

/**
 * Whether the link still works, kept in this page's own state rather than the account's query
 * cache — completing starts a sign-in, which retires that cache and cancels its requests, and a
 * check kept there would go back to loading under the form being submitted. Same reasoning as the
 * invitation page's `usePreview`.
 */
function useLink(token: string, attempt: number): Checked {
  const [checked, setChecked] = React.useState<Checked>({ state: "loading" });
  React.useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    setChecked({ state: "loading" });
    api
      .publicPost<LinkCheck>("/auth/password-reset/check", { token }, controller.signal)
      .then(
        (check) => setChecked({ state: "ready", check }),
        (error: unknown) => {
          if (!controller.signal.aborted) setChecked({ state: "failed", error });
        },
      );
    return () => controller.abort();
  }, [token, attempt]);
  return checked;
}

const schema = z.object({
  password: z.string().min(MIN_PASSWORD, `At least ${MIN_PASSWORD} characters`),
});
type FormValues = z.infer<typeof schema>;

function ResetForm({
  token, email, expiresAt, onGone,
}: {
  token: string;
  email: string;
  expiresAt: string | null;
  /** Re-check the link, so a 410 turns into the "no longer works" screen rather than a red box. */
  onGone: () => void;
}) {
  const { completePasswordReset } = useAuth();
  const [formError, setFormError] = React.useState<string | null>(null);
  const [reveal, setReveal] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      // On success the session changes and this page is remounted signed in; see the page above.
      await completePasswordReset(token, values.password);
    } catch (e) {
      if (e instanceof ApiError && e.status === 410) {
        onGone();
        return;
      }
      setFormError(e instanceof ApiError ? e.message : "Could not reach the server. Try again.");
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      {formError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-danger-4 bg-danger-2 px-3 py-2 text-xs text-danger-11"
        >
          <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
          <span>{formError}</span>
        </div>
      )}

      {/* The address, for a password manager to save the new password against. */}
      <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />

      <Field
        label="New password"
        htmlFor="password"
        hint={`At least ${MIN_PASSWORD} characters. You sign in with it and your email.`}
        error={form.formState.errors.password?.message}
      >
        <Input
          type={reveal ? "text" : "password"}
          autoComplete="new-password"
          autoFocus
          invalid={Boolean(form.formState.errors.password)}
          trailing={
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              className="rounded p-1 text-text-subtle transition-colors hover:bg-surface-hover hover:text-text"
              aria-label={reveal ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {reveal ? <EyeOff /> : <Eye />}
            </button>
          }
          {...form.register("password")}
        />
      </Field>

      <Button
        type="submit"
        variant="default"
        size="lg"
        className="w-full"
        loading={form.formState.isSubmitting}
      >
        Set password and sign in
      </Button>

      {/* Said here rather than in a callout: it is the reason to finish now, not a warning. */}
      <p className="text-center text-xs text-text-subtle">
        {expiresAt
          ? `This link expires ${formatDateTime(expiresAt)}. Everywhere else you are signed in will be signed out.`
          : "Everywhere else you are signed in will be signed out."}
      </p>
    </form>
  );
}
